import React, {useEffect, useRef, useState} from 'react';
import {App, Button, Card, Empty, Input, Pagination, Select, Space, Spin, Tooltip, Typography} from 'antd';
import {
    CheckCircleOutlined,
    DownOutlined,
    ExclamationCircleOutlined,
    HistoryOutlined,
    PauseOutlined,
    PlayCircleOutlined,
    ReloadOutlined,
    RobotOutlined,
    StopOutlined,
    UpOutlined
} from '@ant-design/icons';
import PromptItem from './PromptItem';
import ConversationChainModal from './ConversationChainModal';
import {botService, PromptHistoryResponse} from '../../services/botService';

const {Text} = Typography;
const {Search} = Input;
const {Option} = Select;

interface TrackEvolutionLink {
    trackUnitId: number;
    focusRunId?: number;
}

interface PromptHistoryProps {
    apiKeyId: number | null;
    refreshTrigger: number;
    chainModalVisible?: boolean;
    onOpenChainModal?: (recordId: number) => void;
    onCloseChainModal?: () => void;
    selectedRecordId?: number | null;
    onTransferToChat?: (sessionId: number, userId: string) => void;
    onOpenTrackEvolution?: (link: TrackEvolutionLink) => void;
    publicView?: boolean;
}

/**
 * 单个item的展开状态接口
 * 所有字段都是必需的，确保展开状态正确传递
 */
interface ItemExpandedState {
    promptContent: boolean; // Prompt内容是否展开
    aiResponse: boolean;   // AI模型响应是否展开
}

const PromptHistory: React.FC<PromptHistoryProps> = ({
                                                         apiKeyId,
                                                         refreshTrigger,
                                                         chainModalVisible,
                                                         onOpenChainModal,
                                                         onCloseChainModal,
                                                         selectedRecordId,
                                                         onTransferToChat,
                                                         onOpenTrackEvolution,
                                                         publicView
                                                     }) => {
    const {message: messageApi} = App.useApp();
    const [prompts, setPrompts] = useState<PromptHistoryResponse[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [actionFilter, setActionFilter] = useState<string>('all');
    const [modelFilter, setModelFilter] = useState<string>('all');
    const [callSourceFilter, setCallSourceFilter] = useState<string>('all');
    const [openCloseFilter, setOpenCloseFilter] = useState<string>('all');
    const [availableModels, setAvailableModels] = useState<string[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
    const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const requestSeqRef = useRef(0);
    // 面板折叠状态，默认为false（展开状态）
    const [collapsed, setCollapsed] = useState(false);
    // 保存折叠前的自动刷新状态，用于展开时恢复
    const [prevAutoRefreshEnabled, setPrevAutoRefreshEnabled] = useState(true);

    // 管理每个item的展开状态，使用Map存储避免数据刷新时状态丢失
    const [expandedItems, setExpandedItems] = useState<Record<string, ItemExpandedState>>({});
    // 详情缓存：存储已加载的完整详情数据，避免重复请求
    const [detailCache, setDetailCache] = useState<Map<string, PromptHistoryResponse>>(new Map());

    /**
     * 加载指定决策ID的详情数据
     * 优先从缓存获取，缓存不存在时调用接口加载
     * @param decisionId 决策ID
     * @returns 详情数据，失败时返回null
     */
    const loadPromptDetail = async (decisionId: string): Promise<PromptHistoryResponse | null> => {
        // 缓存命中，直接返回
        if (detailCache.has(decisionId)) {
            return detailCache.get(decisionId) || null;
        }

        try {
            const response = await botService.getPromptDetail(decisionId, apiKeyId ?? undefined);
            if (response.success && response.data) {
                // 存入缓存
                setDetailCache(prev => new Map(prev).set(decisionId, response.data));
                return response.data;
            }
        } catch (error) {
            console.error('加载详情失败:', error);
            messageApi.error('加载详情失败');
        }
        return null;
    };

    /**
     * 合并详情缓存和最新列表数据。
     * 状态、流程等实时字段以最新列表为准；promptContent 等详情字段优先保留缓存值。
     */
    const mergePromptWithFresh = (
        cached: PromptHistoryResponse | undefined,
        fresh: PromptHistoryResponse | undefined
    ): PromptHistoryResponse => {
        if (!fresh) {
            return cached || ({} as PromptHistoryResponse);
        }
        if (!cached) {
            return fresh;
        }
        return {
            ...cached,
            ...fresh,
            promptContent: cached.promptContent ?? fresh.promptContent
        };
    };

    /**
     * 获取prompt项数据
     * 实时状态字段使用最新列表数据，完整详情字段使用detailCache补充
     * @param decisionId 决策ID
     * @returns prompt数据
     */
    const getPromptItem = (decisionId: string): PromptHistoryResponse => {
        const cached = detailCache.get(decisionId);
        const fresh = prompts.find(p => p.decisionId === decisionId);
        return mergePromptWithFresh(cached, fresh);
    };

    /**
     * 删除成功后的本地清理：从列表移除 + 清详情缓存 + 后台刷新
     */
    const handleItemDeleted = (item: PromptHistoryResponse) => {
        setPrompts(prev => prev.filter(p => p.decisionId !== item.decisionId));
        setDetailCache(prev => {
            const next = new Map(prev);
            next.delete(item.decisionId);
            return next;
        });
        fetchPromptHistory(true);
    };

    /**
     * 更新指定item的展开状态
     * 展开时自动加载详情数据
     * @param itemId 项目ID
     * @param key 状态key（promptContent/aiResponse）
     * @param value 是否展开
     */
    const updateItemExpandedState = async (itemId: string, key: keyof ItemExpandedState, value: boolean) => {
        // 如果是展开操作且详情未加载，先加载详情
        if (value && !detailCache.has(itemId)) {
            await loadPromptDetail(itemId);
        }

        setExpandedItems(prev => {
            const currentItemState = prev[itemId] || {
                promptContent: false,
                aiResponse: false
            };

            return {
                ...prev,
                [itemId]: {
                    ...currentItemState,
                    [key]: value
                }
            };
        });
    };

    /**
     * 确保指定item的展开状态对象完整
     * 如果状态不存在或字段缺失，返回默认的完整状态对象
     * @param decisionId 决策ID
     * @returns 完整的展开状态对象
     */
    const getCompleteExpandedState = (decisionId: string): ItemExpandedState => {
        const existingState = expandedItems[decisionId];

        // 如果状态已存在且完整，直接返回
        if (existingState &&
            typeof existingState.promptContent === 'boolean' &&
            typeof existingState.aiResponse === 'boolean') {
            return existingState;
        }

        // 否则返回默认的完整状态（全部折叠）
        return {
            promptContent: existingState?.promptContent || false,
            aiResponse: existingState?.aiResponse || false
        };
    };

    /**
     * 获取prompt历史
     * @param isBackground 是否为后台静默模式(不显示loading,不弹错误提示)
     */
    const fetchPromptHistory = async (isBackground = false) => {
        const requestSeq = ++requestSeqRef.current;
        if (!apiKeyId) {
            setPrompts([]);
            setTotal(0);
            return;
        }

        try {
            // 静默模式不显示loading状态
            if (!isBackground) {
                setLoading(true);
            }

            const response = await botService.getPromptHistory(
                apiKeyId,
                pageSize,
                actionFilter !== 'all' ? actionFilter : undefined,
                modelFilter !== 'all' ? modelFilter : undefined,
                callSourceFilter !== 'all' ? callSourceFilter : undefined,
                openCloseFilter !== 'all' ? openCloseFilter : undefined,
                statusFilter !== 'all' ? statusFilter : undefined,
                searchText.trim() || undefined,
                currentPage,
                pageSize
            );

            if (requestSeq !== requestSeqRef.current) {
                return;
            }

            if (response.success && response.data) {
                setPrompts(response.data);
                // 同步详情缓存中的实时状态，避免已展开记录长时间停留在旧状态
                setDetailCache(prev => {
                    const next = new Map(prev);
                    response.data.forEach(fresh => {
                        const cached = next.get(fresh.decisionId);
                        if (cached) {
                            next.set(fresh.decisionId, mergePromptWithFresh(cached, fresh));
                        }
                    });
                    return next;
                });
                setTotal(typeof (response as any).total === 'number' ? (response as any).total : response.data.length);

                // 提取所有可用的模型名称
                const models = Array.from(new Set(response.data.map(p => p.modelName).filter(Boolean)));
                setAvailableModels(models);
            } else {
                // 静默模式下不显示错误消息,避免打扰用户
                if (!isBackground) {
                    messageApi.error(response.message || '获取prompt历史失败');
                }
                setPrompts([]);
                setTotal(0);
            }
        } catch (error) {
            // console.error('获取prompt历史失败:', error);
            // 静默模式下不显示错误消息
            if (!isBackground) {
                messageApi.error('获取prompt历史失败');
            }
            setPrompts([]);
            setTotal(0);
        } finally {
            // 静默模式下不重置loading状态
            if (!isBackground && requestSeq === requestSeqRef.current) {
                setLoading(false);
            }
        }
    };

    const getCurrentPagePrompts = () => prompts;

    // 刷新数据
    const handleRefresh = () => {
        if (currentPage !== 1) {
            setCurrentPage(1);
        } else {
            fetchPromptHistory(false); // 手动刷新时显示loading
        }
    };

    /**
     * 切换面板折叠/展开状态
     * 折叠时停止定时刷新，展开时恢复之前的刷新状态
     */
    const handleToggleCollapse = () => {
        if (!collapsed) {
            // 准备折叠时，保存当前刷新状态并停止刷新
            setPrevAutoRefreshEnabled(autoRefreshEnabled);
            setAutoRefreshEnabled(false);
        } else {
            // 展开时，恢复之前的刷新状态
            setAutoRefreshEnabled(prevAutoRefreshEnabled);
        }
        setCollapsed(!collapsed);
    };

    // 调用大模型
    // const handleCallModel = async (decisionId: string) => {
    //     try {
    //         // 这里可以调用后端API来处理未完成的prompt
    //         messageApi.info('调用大模型功能开发中...');
    //     } catch (error) {
    //         console.error('调用大模型失败:', error);
    //         messageApi.error('调用大模型失败');
    //     }
    // };

    // 页面变化
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    // 重置过滤器
    const handleResetFilters = () => {
        setSearchText('');
        setStatusFilter('all');
        setActionFilter('all');
        setModelFilter('all');
        setCallSourceFilter('all');
        setOpenCloseFilter('all');
        setCurrentPage(1);
    };

    const getOpenCloseFilterSelectValue = (): string => {
        if (openCloseFilter === 'all') {
            return 'all';
        }
        if (actionFilter === 'BUY') {
            return openCloseFilter === 'CLOSE' ? 'BUY_CLOSE' : 'BUY_OPEN';
        }
        if (actionFilter === 'SELL') {
            return openCloseFilter === 'CLOSE' ? 'SELL_CLOSE' : 'SELL_OPEN';
        }
        return openCloseFilter;
    };

    const handleOpenCloseFilterChange = (value: string) => {
        if (value === 'BUY_OPEN' || value === 'BUY_CLOSE') {
            setActionFilter('BUY');
            setOpenCloseFilter(value === 'BUY_CLOSE' ? 'CLOSE' : 'OPEN');
        } else if (value === 'SELL_OPEN' || value === 'SELL_CLOSE') {
            setActionFilter('SELL');
            setOpenCloseFilter(value === 'SELL_CLOSE' ? 'CLOSE' : 'OPEN');
        } else {
            setActionFilter(value);
            setOpenCloseFilter('all');
        }
        setCurrentPage(1);
    };

    // 处理会话链路点击,打开会话链路弹窗
    const handleParentIdClick = (recordId: number) => {
        onOpenChainModal?.(recordId);
    };

    const handleTrackEvolutionClick = (link: { trackUnitId: number; trackRunId: number }) => {
        onOpenTrackEvolution?.({trackUnitId: link.trackUnitId, focusRunId: link.trackRunId});
    };

    // 关闭会话链路弹窗
    const handleChainModalClose = () => {
        onCloseChainModal?.();
    };

    // 总数以后端筛选后的 total 为准
    const filteredTotal = total;

    useEffect(() => {
        // 展开 Item 时仍保留后台定时刷新：fetchPromptHistory(true) 为静默刷新，
        // 仅同步 prompts 与 detailCache 最新状态，不打断展开内容的交互。
        if (autoRefreshEnabled && apiKeyId && !collapsed) {
            const interval = setInterval(() => {
                fetchPromptHistory(true);
            }, 30000);
            refreshIntervalRef.current = interval;
            return () => {
                clearInterval(interval);
                refreshIntervalRef.current = null;
            };
        }
        return undefined;
    }, [
        autoRefreshEnabled,
        apiKeyId,
        collapsed,
        actionFilter,
        modelFilter,
        callSourceFilter,
        openCloseFilter,
        statusFilter,
        searchText,
        currentPage
    ]);

    useEffect(() => {
        if (apiKeyId) {
            fetchPromptHistory(false);
        }
    }, [
        apiKeyId,
        refreshTrigger,
        actionFilter,
        modelFilter,
        callSourceFilter,
        openCloseFilter,
        statusFilter,
        searchText,
        currentPage
    ]);

    return (
        <>
            <Card
                title={
                    <Space>
                        <HistoryOutlined style={{color: 'var(--pt-primary)'}}/>
                        <span>执行记录</span>
                        <Text style={{fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)'}}>
                            ({total} 条记录)
                        </Text>
                    </Space>
                }
                extra={
                    <Space>
                        <Tooltip title={autoRefreshEnabled ? "关闭自动刷新" : "开启自动刷新"}>
                            <Button
                                type="text"
                                icon={autoRefreshEnabled ? <PauseOutlined/> : <PlayCircleOutlined/>}
                                onClick={() => setAutoRefreshEnabled(!autoRefreshEnabled)}
                                style={{color: autoRefreshEnabled ? 'rgba(255, 193, 7, 0.8)' : 'rgba(255, 255, 255, 0.6)'}}
                            />
                        </Tooltip>
                        <Tooltip title="手动刷新数据">
                            <Button
                                type="text"
                                icon={<ReloadOutlined/>}
                                onClick={handleRefresh}
                                loading={loading}
                                style={{color: 'rgba(255, 255, 255, 0.6)'}}
                            />
                        </Tooltip>
                        <Tooltip title={collapsed ? "展开面板" : "折叠面板"}>
                            <Button
                                type="text"
                                icon={collapsed ? <DownOutlined/> : <UpOutlined/>}
                                onClick={handleToggleCollapse}
                                style={{color: collapsed ? 'rgba(255, 255, 255, 0.6)' : 'var(--pt-primary)'}}
                            />
                        </Tooltip>
                    </Space>
                }
                style={{
                    height: collapsed ? 'auto' : '100%',
                    maxHeight: collapsed ? 'auto' : '78vh',
                    backgroundColor: 'var(--pt-bg-panel)',
                    border: '1px solid rgba(255,255,255,0.13)',
                    borderRadius: '8px',
                    display: 'flex',
                    flexDirection: 'column'
                }}
                styles={{
                    header: {
                        backgroundColor: '#1a1a1a',
                        borderBottom: '1px solid rgba(255,255,255,0.13)',
                        color: 'rgba(255, 255, 255, 0.95)'
                    },
                    body: collapsed ? {
                        height: '30px',
                        minHeight: '30px',
                        padding: 0,
                        overflow: 'hidden'
                    } : {
                        padding: '16px',
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden'
                    }
                }}
            >
                {/* 面板内容：折叠时隐藏，展开时显示 */}
                {!collapsed && (
                    <>
                        {/* 搜索和过滤器 */}
                        {/* 搜索和过滤器 */}
                        <div style={{marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '12px'}}>
                            {/* 第一行：搜索框、状态、决策 */}
                            <div style={{display: 'flex', gap: '12px', alignItems: 'center'}}>
                                <Search
                                    placeholder="搜索当前可见Prompt/用户设定、决策推理、合约代码、模型名称、调用来源..."
                                    value={searchText}
                                    onChange={(e) => {
                                        setSearchText(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    style={{width: 320}}
                                    allowClear
                                />

                                <Select
                                    value={statusFilter}
                                    onChange={(value) => {
                                        setStatusFilter(value);
                                        setCurrentPage(1);
                                    }}
                                    style={{width: 120}}
                                    placeholder="状态筛选"
                                >
                                    <Option value="all">全部状态</Option>
                                    <Option value="SUCCESS">
                                        <Space>
                                            <CheckCircleOutlined style={{color: 'var(--pt-bull)'}}/>
                                            成功
                                        </Space>
                                    </Option>
                                    <Option value="PROCESSING">
                                        <Space>
                                            <RobotOutlined style={{color: 'var(--pt-primary)'}}/>
                                            处理中
                                        </Space>
                                    </Option>
                                    <Option value="FAILED">
                                        <Space>
                                            <ExclamationCircleOutlined style={{color: 'var(--pt-bear)'}}/>
                                            失败
                                        </Space>
                                    </Option>
                                    <Option value="AGENT_DISABLED">
                                        <Space>
                                            <StopOutlined style={{color: 'var(--pt-amber)'}}/>
                                            已禁用
                                        </Space>
                                    </Option>
                                </Select>

                                <Select
                                    value={actionFilter}
                                    onChange={(value) => {
                                        setActionFilter(value);
                                        setOpenCloseFilter('all');
                                        setCurrentPage(1);
                                    }}
                                    style={{width: 140}}
                                    placeholder="决策筛选"
                                >
                                    <Option value="all">全部决策</Option>
                                    <Option value="SELL">
                                        <Space>
                                            <span>📉</span>
                                            卖出
                                        </Space>
                                    </Option>
                                    <Option value="BUY">
                                        <Space>
                                            <span>📈</span>
                                            买入
                                        </Space>
                                    </Option>
                                    <Option value="HOLD">
                                        <Space>
                                            <span>⏸️</span>
                                            持有
                                        </Space>
                                    </Option>
                                    <Option value="WATCH">
                                        <Space>
                                            <span>👁️</span>
                                            观望
                                        </Space>
                                    </Option>
                                    <Option value="ATTENTION">
                                        <Space>
                                            <span>⚠️</span>
                                            关注
                                        </Space>
                                    </Option>
                                    <Option value="CANCEL_ORDER">
                                        <Space>
                                            <span>❌</span>
                                            取消订单
                                        </Space>
                                    </Option>
                                    <Option value="QUERY">
                                        <Space>
                                            <span>🔍</span>
                                            查询
                                        </Space>
                                    </Option>
                                </Select>
                            </div>

                            {/* 第二行：模型、来源、开/平仓 */}
                            <div style={{display: 'flex', gap: '12px', alignItems: 'center'}}>
                                <Select
                                    value={modelFilter}
                                    onChange={(value) => {
                                        setModelFilter(value);
                                        setCurrentPage(1);
                                    }}
                                    style={{width: 140}}
                                    placeholder="模型筛选"
                                >
                                    <Option value="all">全部模型</Option>
                                    {availableModels.map(model => (
                                        <Option key={model} value={model}>
                                            {model}
                                        </Option>
                                    ))}
                                </Select>

                                <Select
                                    value={callSourceFilter}
                                    onChange={(value) => {
                                        setCallSourceFilter(value);
                                        setCurrentPage(1);
                                    }}
                                    style={{width: 140}}
                                    placeholder="调用来源"
                                >
                                    <Option value="all">全部来源</Option>
                                    <Option value="SCHEDULED">
                                        <Space>
                                            <span>⏰</span>
                                            AI
                                        </Space>
                                    </Option>
                                    <Option value="MANUAL">
                                        <Space>
                                            <span>👆</span>
                                            HUM
                                        </Space>
                                    </Option>
                                    <Option value="DIRECT">
                                        <Space>
                                            <span>🔗</span>
                                            BOT
                                        </Space>
                                    </Option>
                                    <Option value="AGENT_TRIGGER">
                                        <Space>
                                            <span>🤖</span>
                                            Agent
                                        </Space>
                                    </Option>
                                </Select>

                                <Select
                                    value={getOpenCloseFilterSelectValue()}
                                    onChange={handleOpenCloseFilterChange}
                                    style={{width: 140}}
                                    placeholder="交易方向"
                                >
                                    <Option value="all">全部</Option>
                                    <Option value="BUY_OPEN">
                                        <Space>
                                            <span>📈</span>
                                            买入开多
                                        </Space>
                                    </Option>
                                    <Option value="SELL_CLOSE">
                                        <Space>
                                            <span>📉</span>
                                            卖出平多
                                        </Space>
                                    </Option>
                                    <Option value="SELL_OPEN">
                                        <Space>
                                            <span>📉</span>
                                            卖出开空
                                        </Space>
                                    </Option>
                                    <Option value="BUY_CLOSE">
                                        <Space>
                                            <span>📈</span>
                                            买入平空
                                        </Space>
                                    </Option>
                                </Select>

                                {(searchText || statusFilter !== 'all' || actionFilter !== 'all' || modelFilter !== 'all' || callSourceFilter !== 'all' || openCloseFilter !== 'all') && (
                                    <Button size="small" onClick={handleResetFilters}>
                                        重置筛选
                                    </Button>
                                )}
                            </div>
                        </div>

                        {/* Prompt列表 */}
                        <div style={{
                            flex: 1,
                            overflowY: 'auto',
                            paddingRight: '4px' // 滚动条的空间
                        }}>
                            {loading ? (
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: '200px'
                                }}>
                                    <Spin size="large">
                                        <div style={{marginTop: 8, color: '#999'}}>加载中...</div>
                                    </Spin>
                                </div>
                            ) : !apiKeyId ? (
                                <Empty
                                    description="请先选择API Key"
                                    style={{marginTop: '60px'}}
                                />
                            ) : getCurrentPagePrompts().length === 0 ? (
                                <Empty
                                    description={searchText || statusFilter !== 'all' ? '没有找到匹配的记录' : '暂无prompt历史记录'}
                                    style={{marginTop: '60px'}}
                                />
                            ) : (
                                <div>
                                    {getCurrentPagePrompts().map((prompt) => {
                                        // 优先使用缓存中的详情，否则使用列表中的概览数据
                                        const promptData = getPromptItem(prompt.decisionId);
                                        return (
                                            <PromptItem
                                                key={prompt.decisionId}
                                                item={promptData}
                                                // onCallModel={handleCallModel}
                                                expandedState={getCompleteExpandedState(prompt.decisionId)}
                                                onExpandedStateChange={(key, value) =>
                                                    updateItemExpandedState(prompt.decisionId, key, value)
                                                }
                                                onParentIdClick={handleParentIdClick}
                                                onTrackEvolutionClick={publicView ? undefined : handleTrackEvolutionClick}
                                                hideTrackActionTag
                                                publicView={publicView}
                                                apiKeyId={apiKeyId}
                                                deletable={!publicView}
                                                onDelete={handleItemDeleted}
                                            />
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* 分页 */}
                        {filteredTotal > 0 && (
                            <div style={{
                                marginTop: '16px',
                                display: 'flex',
                                justifyContent: 'center',
                                paddingTop: '16px',
                                borderTop: '1px solid rgba(255,255,255,0.13)'
                            }}>
                                <Pagination
                                    current={currentPage}
                                    total={total}
                                    pageSize={pageSize}
                                    onChange={handlePageChange}
                                    showSizeChanger={false}
                                    showQuickJumper
                                    showTotal={(total, range) =>
                                        `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`
                                    }
                                    style={{
                                        color: 'rgba(255, 255, 255, 0.88)'
                                    }}
                                />
                            </div>
                        )}
                    </>
                )}
            </Card>

            {/* 会话链路弹窗 */}
            <ConversationChainModal
                visible={!!chainModalVisible}
                onClose={handleChainModalClose}
                recordId={selectedRecordId || null}
                apiKeyId={apiKeyId}
                onTransfer={onTransferToChat}
                publicView={publicView}
            />
        </>
    );
};

export default PromptHistory;
